import { useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Trash } from "lucide-react";
import { z } from "zod";

import { FormField } from "components/form/FormField/FormField";
import { Select } from "components/ui/Select/Select";
import { onInvalidSubmit } from "utils/reportFormErrors";

import style from "./AddFormForm.module.css";
import { CervicalCheckForm } from "./Forms/CervicalCheck/CervicalCheck";
import { SocialWelfareECertForm } from "./Forms/SocialWelfareECert/SocialWelfareECertForm";

const formSchema = z.object({
  type: z.string().min(1, { message: "Please select letter type." }),
});

export const AddFormForm = ({ consultation, patient, healthlinkEnabled = true }) => {
  const [items, setItems] = useState([]);

  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: { type: "social-welfare-e-cert" },
  });

  const selectedType = form.watch("type");
  const isTypeAlreadyOpen = items.some((item) => item.type === selectedType);

  const handleFormSubmit = async (values) => {
    if (items.some((item) => item.type === values.type)) return;
    setItems([...items, { type: values.type, id: crypto.randomUUID() }]);
  };

  const handleRemoveItem = (id) => {
    setItems((prev) => prev.filter((item) => item.id !== id));
  };

  return (
    <>
      {!healthlinkEnabled && (
        <div className={style.healthlinkWarning}>
          Healthlink is not enabled for this practice. Enable it in Settings to submit forms.
        </div>
      )}
      <FormProvider {...form}>
        <form
          className={style.addLetterForm}
          onSubmit={form.handleSubmit(handleFormSubmit, onInvalidSubmit("AddFormForm"))}
          style={{
            paddingBottom: items.length > 0 && "20px",
            borderBottom: items.length > 0 && "1px solid var(--gray-200)",
          }}
        >
          <div
            className={style.inputGroupGrid}
            style={{
              justifyContent: "space-between",
            }}
          >
            <FormField name="type">
              <label className={style.addLetterFormLabel}>Type</label>
              <Select
                name="type"
                options={[
                  {
                    label: "Social Welfare E-Cert",
                    value: "social-welfare-e-cert",
                  },
                ]}
                value={form.watch("type")}
                onChange={(value) => form.setValue("type", value)}
                placeholder={null}
                fullWidth
              />
            </FormField>
            {!isTypeAlreadyOpen && (
              <button
                className={style.addFormButton}
                onClick={form.handleSubmit(handleFormSubmit, onInvalidSubmit("AddFormForm"))}
                disabled={!healthlinkEnabled}
              >
                Add form
              </button>
            )}
          </div>
        </form>
      </FormProvider>
      <div
        style={{
          marginTop: items.length > 0 && "24px",
          display: "flex",
          flexDirection: "column",
          gap: "24px",
        }}
      >
        {items.map((item) => (
          <div key={item.id} className={style.formItem}>
            <button
              onClick={() => handleRemoveItem(item.id)}
              aria-label="Remove form"
              className={style.removeFormButton}
            >
              <Trash />
            </button>
            {item.type === "social-welfare-e-cert" && (
              <SocialWelfareECertForm
                onSuccess={() => handleRemoveItem(item.id)}
                consultation={consultation}
                patient={patient}
              />
            )}
            {item.type === "cervical-check" && (
              <CervicalCheckForm initialData={{}} />
            )}
          </div>
        ))}
      </div>
    </>
  );
};
